import axios, { AxiosInstance } from "axios";
import authorizeService from "../../../Identity/Utilities/AuthorizeService";
import type { AnomalyBandWithForm } from "../Components/Overview/Dialogs/AnomalyDetector/AnomalyBand/types";
import type {
  AnomalyLookupsPayload,
  AnomalyNotePayload,
  CreateAnchorBackendPayload,
  InsightBackendPayload,
  MLResultPair,
  MlAnchorDto,
  MlDto,
  UpdateAnchorNoteBackendPayload,
  UpdateAnchorBackendPayload,
  UpdateAnomalyBackendPayload,
  UpdateAnomalyNoteBackendPayload,
} from "../Components/Insights/types";
import {
  isOngoingEndDate,
  normalizeCriticality,
  normalizeStreamValues,
} from "../Components/Overview/Dialogs/AnomalyDetector/AnomalyDetectorForm.utils";

class AnomalyApiService {
  axiosInstance: AxiosInstance;
  constructor() {
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_EVENT_SERVICE_URL,
    });
  }

  private getValue<T>(data: unknown, ...keys: string[]): T | undefined {
    if (!data || typeof data !== "object") {
      return undefined;
    }

    for (const key of keys) {
      const value = (data as Record<string, unknown>)[key];
      if (value != null) {
        return value as T;
      }
    }

    return undefined;
  }

  private getArrayValue<T>(data: unknown, ...keys: string[]): T[] {
    const value = this.getValue<unknown>(data, ...keys);
    return Array.isArray(value) ? (value as T[]) : [];
  }

  private normalizeNote(item: unknown) {
    return {
      id: this.getValue<string>(item, "id", "Id", "noteId", "NoteId"),
      timestamp: this.getValue<string>(item, "timestamp", "Timestamp") ?? "",
      user: this.getValue<string>(item, "user", "User") ?? "",
      comment: this.getValue<string>(item, "comment", "Comment") ?? "",
    };
  }

  private normalizeNotes(items: unknown[]): AnomalyNotePayload[] {
    return items
      .map((note) => this.normalizeNote(note))
      .filter((note) => note.id || note.timestamp || note.user || note.comment);
  }

  private async getAuthorizedHeaders(includeJsonContentType = false) {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
    };

    if (includeJsonContentType) {
      headers["Content-Type"] = "application/json";
    }

    return headers;
  }

  private normalizeRevisionHistory(item: unknown) {
    return {
      timestamp: this.getValue<string>(item, "timestamp", "Timestamp") ?? "",
      user: this.getValue<string>(item, "user", "User") ?? "",
      change: this.getValue<string>(item, "change", "Change") ?? "",
    };
  }

  private normalizeFormPayload(form: unknown) {
    return {
      criticality: this.getValue<string>(form, "criticality", "Criticality") ?? "",
      indicators: this.getArrayValue<string>(form, "indicators", "Indicators"),
      rootCauses: this.getArrayValue<string>(form, "rootCauses", "RootCauses"),
      actions: this.getArrayValue<string>(form, "actions", "Actions"),
      confidence: Number(this.getValue<number>(form, "confidence", "Confidence") ?? 0),
      estimatedSavings: Number(this.getValue<number>(form, "estimatedSavings", "EstimatedSavings") ?? 0),
    };
  }

  private normalizeMlResultsPayload(mlResults: unknown): MLResultPair[] {
    if (!Array.isArray(mlResults)) {
      return [];
    }

    return mlResults
      .map((pair) => ({
        k: String(this.getValue<string>(pair, "k", "K", "key", "Key") ?? ""),
        v: String(this.getValue<string>(pair, "v", "V", "value", "Value") ?? ""),
      }))
      .filter((pair) => pair.k !== "");
  }

  private normalizeAnomaly(item: unknown): InsightBackendPayload {
    const form = this.getValue<unknown>(item, "form", "Form");
    const mlResults = this.getValue<unknown>(item, "mlResults", "MLResults");
    const notes = this.normalizeNotes(this.getArrayValue<unknown>(item, "notes", "Notes"));
    const legacyNotes = this.normalizeNotes(this.getArrayValue<unknown>(form, "notes", "Notes"));

    return {
      id: this.getValue<string>(item, "id", "Id"),
      machineId: this.getValue<string>(item, "machineId", "MachineId") ?? "",
      pointId: this.getValue<string>(item, "pointId", "PointId") ?? "",
      serialNo: this.getValue<string>(item, "serialNo", "SerialNo"),
      user: this.getValue<string>(item, "user", "User") ?? "",
      userId: this.getValue<string>(item, "userId", "UserId") ?? "",
      startDate: this.getValue<string>(item, "startDate", "StartDate") ?? "",
      endDate: this.getValue<string>(item, "endDate", "EndDate") ?? "",
      anomalyId: this.getValue<string>(item, "anomalyId", "AnomalyId") ?? "",
      streams: normalizeStreamValues(this.getArrayValue<string>(item, "streams", "Streams")),
      form: this.normalizeFormPayload(form),
      notes: notes.length > 0 ? notes : legacyNotes,
      revisionHistory: this.getArrayValue<unknown>(item, "revisionHistory", "RevisionHistory")
        .map((entry) => this.normalizeRevisionHistory(entry)),
      mlResults: this.normalizeMlResultsPayload(mlResults),
    };
  }

  private normalizeAnchor(item: unknown): MlAnchorDto {
    const form = this.getValue<unknown>(item, "form", "Form");
    const mlResults = this.getValue<unknown>(item, "mlResults", "MLResults");

    return {
      id: this.getValue<string>(item, "id", "Id") ?? "",
      machineId: this.getValue<string>(item, "machineId", "MachineId") ?? "",
      pointId: this.getValue<string>(item, "pointId", "PointId") ?? "",
      user: this.getValue<string>(item, "user", "User") ?? "",
      userId: this.getValue<string>(item, "userId", "UserId") ?? "",
      startDate: this.getValue<string>(item, "startDate", "StartDate") ?? "",
      endDate: this.getValue<string>(item, "endDate", "EndDate") ?? "",
      anomalyId: this.getValue<string>(item, "anomalyId", "AnomalyId"),
      streams: normalizeStreamValues(this.getArrayValue<string>(item, "streams", "Streams")),
      form: this.normalizeFormPayload(form),
      notes: this.normalizeNotes(this.getArrayValue<unknown>(item, "notes", "Notes")),
      revisionHistory: this.getArrayValue<unknown>(item, "revisionHistory", "RevisionHistory")
        .map((entry) => this.normalizeRevisionHistory(entry)),
      mlResults: this.normalizeMlResultsPayload(mlResults),
    };
  }

  private normalizeMlDto(data: unknown): MlDto {
    if (Array.isArray(data)) {
      return {
        anchors: [],
        anomalies: data.map((item) => this.normalizeAnomaly(item)),
      };
    }

    return {
      anchors: this.getArrayValue<unknown>(data, "anchors", "Anchors").map((item) => this.normalizeAnchor(item)),
      anomalies: this.getArrayValue<unknown>(data, "anomalies", "Anomalies").map((item) => this.normalizeAnomaly(item)),
    };
  }

  private normalizeNotesPayload(data: unknown): AnomalyNotePayload[] {
    const rawNotes = Array.isArray(data) ? data : this.getArrayValue<unknown>(data, "notes", "Notes");

    return this.normalizeNotes(rawNotes);
  }

  private async getPointMlPayload(pointId: string): Promise<MlDto> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/ml/points/${pointId}/anomalies`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });

    return this.normalizeMlDto(response.data);
  }

  private getLookupValues(data: any, ...keys: string[]): string[] {
    const rawValues = keys.reduce<unknown>((result, key) => {
      if (result != null) {
        return result;
      }

      return data?.[key];
    }, undefined) ?? [];

    if (!Array.isArray(rawValues)) {
      return [];
    }

    return rawValues
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          return item.label ?? item.Label ?? item.value ?? item.Value ?? item.text ?? item.Text ?? item.name ?? item.Name ?? "";
        }

        return "";
      })
      .filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  }

  private getLookupOptions(data: any, ...keys: string[]) {
    const rawValues = keys.reduce<unknown>((result, key) => {
      if (result != null) {
        return result;
      }

      return data?.[key];
    }, undefined) ?? [];

    if (!Array.isArray(rawValues)) {
      return [];
    }

    return rawValues
      .map((item) => {
        if (typeof item === "string") {
          return { text: item, value: item };
        }

        if (item && typeof item === "object") {
          const value =
            item.value ?? item.Value ?? item.id ?? item.Id ?? item.key ?? item.Key ?? item.code ?? item.Code ?? "";
          const text =
            item.label ?? item.Label ?? item.text ?? item.Text ?? item.name ?? item.Name ?? value;

          if (typeof value === "string" && value.trim().length > 0) {
            return {
              text: typeof text === "string" && text.trim().length > 0 ? text : value,
              value,
            };
          }
        }

        return undefined;
      })
      .filter(
        (item): item is { text: string; value: string } =>
          !!item && item.value.trim().length > 0 && item.text.trim().length > 0
      );
  }

  async getAnomalyLookups(): Promise<AnomalyLookupsPayload | null> {
    try {
      const response = await this.axiosInstance({
        method: "GET",
        url: "web/v1/ml/anomalies/lookups",
        headers: {
          Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
        },
      });

      return {
        indicators: this.getLookupOptions(response.data, "indicators", "indictors"),
        rootCauses: this.getLookupOptions(response.data, "rootCauses"),
        actions: this.getLookupOptions(response.data, "actions"),
      };
    } catch {
      return null;
    }
  }

  async getPointAnomaliesPayload(pointId: string): Promise<InsightBackendPayload[]> {
    try {
      const mlData = await this.getPointMlPayload(pointId);
      return mlData.anomalies;
    } catch {
      return [];
    }
  }

  async getAssetAnomaliesPayload(assetId: string): Promise<InsightBackendPayload[]> {
    try {
      const response = await this.axiosInstance({
        method: "GET",
        url: `web/v1/ml/assets/${assetId}/anomalies`,
        headers: {
          Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
        },
      });

      return this.normalizeMlDto(response.data).anomalies;
    } catch {
      return [];
    }
  }

  async getAssetAnchorsPayload(assetId: string): Promise<MlAnchorDto[]> {
    try {
      const response = await this.axiosInstance({
        method: "GET",
        url: `web/v1/ml/assets/${assetId}/anchors`,
        headers: {
          Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
        },
      });

      if (Array.isArray(response.data)) {
        return response.data.map((item) => this.normalizeAnchor(item));
      }

      return this.normalizeMlDto(response.data).anchors;
    } catch {
      return [];
    }
  }

  // Site-level rows come lean: notes/revisionHistory/mlResults arrive empty and are fetched
  // on demand via getAnomalyPayload/getAnchorPayload when an item is selected.
  async getSiteAnomaliesPayload(startDate: Date, endDate: Date): Promise<InsightBackendPayload[]> {
    try {
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      const response = await this.axiosInstance({
        method: "GET",
        url: `web/v1/ml/sites/anomalies?${params.toString()}`,
        headers: await this.getAuthorizedHeaders(),
      });

      return this.normalizeMlDto(response.data).anomalies;
    } catch {
      return [];
    }
  }

  async getSiteAnchorsPayload(startDate: Date, endDate: Date): Promise<MlAnchorDto[]> {
    try {
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      const response = await this.axiosInstance({
        method: "GET",
        url: `web/v1/ml/sites/anchors?${params.toString()}`,
        headers: await this.getAuthorizedHeaders(),
      });

      if (Array.isArray(response.data)) {
        return response.data.map((item) => this.normalizeAnchor(item));
      }

      return this.normalizeMlDto(response.data).anchors;
    } catch {
      return [];
    }
  }

  async getAnomalyPayload(anomalyId: string): Promise<InsightBackendPayload | null> {
    try {
      const response = await this.axiosInstance({
        method: "GET",
        url: `web/v1/ml/anomalies/${anomalyId}`,
        headers: await this.getAuthorizedHeaders(),
      });

      return this.normalizeAnomaly(response.data);
    } catch {
      return null;
    }
  }

  async getAnchorPayload(anchorId: string): Promise<MlAnchorDto | null> {
    try {
      const response = await this.axiosInstance({
        method: "GET",
        url: `web/v1/ml/anchors/${anchorId}`,
        headers: await this.getAuthorizedHeaders(),
      });

      return this.normalizeAnchor(response.data);
    } catch {
      return null;
    }
  }

  async getPointAnchorsPayload(pointId: string): Promise<MlAnchorDto[]> {
    try {
      const response = await this.axiosInstance({
        method: "GET",
        url: `web/v1/ml/points/${pointId}/anchors`,
        headers: {
          Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
        },
      });

      if (Array.isArray(response.data)) {
        return response.data.map((item) => this.normalizeAnchor(item));
      }

      return this.normalizeMlDto(response.data).anchors;
    } catch {
      return [];
    }
  }

  async getPointAnomalies(pointId: string): Promise<AnomalyBandWithForm[]> {
    try {
      const [anomalies, anchors] = await Promise.all([
        this.getPointAnomaliesPayload(pointId),
        this.getPointAnchorsPayload(pointId),
      ]);

      const anomalyBands = anomalies.map((item: InsightBackendPayload, index: number) => {
        const persistedId = item.id || undefined;
        const localBandId = persistedId ?? `band-${pointId}-${index}`;
        const legacyForm = item.form as typeof item.form & { notes?: AnomalyBandWithForm["notes"] };
        const ongoing = isOngoingEndDate(item.endDate);

        return {
          id: localBandId,
          bandType: "anomaly" as const,
          persistedId,
          anomalyId: item.anomalyId || undefined,
          notes: item.notes ?? legacyForm.notes ?? [],
          pendingNotes: [],
          startTime: Math.floor(new Date(item.startDate).getTime() / 1000),
          endTime: ongoing
            ? Math.floor(Date.now() / 1000)
            : Math.floor(new Date(item.endDate as string).getTime() / 1000),
          ongoing,
          streams: item.streams ?? [],
          criticality: normalizeCriticality(item.form.criticality) || undefined,
          indicators: item.form.indicators ?? [],
          rootCauses: item.form.rootCauses ?? [],
          actions: item.form.actions ?? [],
          mlResults: item.mlResults,
          confidence: item.form.confidence ?? undefined,
          savings: item.form.estimatedSavings ?? undefined,
          userNotes: '',
          createdByUserId: item.userId || undefined,
          createdByUserName: item.user || undefined,
        };
      });

      const anchorBands = anchors.map((item: MlAnchorDto, index: number) => {
        const persistedId = item.id || undefined;
        const localBandId = persistedId ?? `anchor-${pointId}-${index}`;

        return {
          id: localBandId,
          bandType: "anchor" as const,
          persistedId,
          anomalyId: item.anomalyId || undefined,
          notes: item.notes ?? [],
          pendingNotes: [],
          revisionHistory: item.revisionHistory ?? [],
          startTime: Math.floor(new Date(item.startDate).getTime() / 1000),
          endTime: Math.floor(new Date(item.endDate).getTime() / 1000),
          streams: item.streams ?? [],
          criticality: normalizeCriticality(item.form.criticality) || undefined,
          indicators: item.form.indicators ?? [],
          rootCauses: item.form.rootCauses ?? [],
          actions: item.form.actions ?? [],
          mlResults: item.mlResults,
          confidence: item.form.confidence ?? undefined,
          savings: item.form.estimatedSavings ?? undefined,
          userNotes: '',
          createdByUserId: item.userId || undefined,
          createdByUserName: item.user || undefined,
        };
      });

      return [...anomalyBands, ...anchorBands];
    } catch {
      return [];
    }
  }

  async createAnomaly(anomaly: InsightBackendPayload): Promise<void> {
    await this.axiosInstance({
      method: "POST",
      url: "web/v1/ml/anomalies",
      headers: await this.getAuthorizedHeaders(true),
      data: anomaly,
    });
  }

  async createAnchor(anchor: CreateAnchorBackendPayload): Promise<void> {
    await this.axiosInstance({
      method: "POST",
      url: "web/v1/ml/anchors",
      headers: await this.getAuthorizedHeaders(true),
      data: anchor,
    });
  }

  async updateAnomaly(anomaly: UpdateAnomalyBackendPayload, persistedId: string): Promise<void> {
    await this.axiosInstance({
      method: "PATCH",
      url: `web/v1/ml/anomalies/${persistedId}`,
      headers: await this.getAuthorizedHeaders(true),
      data: anomaly,
    });
  }

  async updateAnchor(anchor: UpdateAnchorBackendPayload, anchorId: string): Promise<void> {
    await this.axiosInstance({
      method: "PATCH",
      url: `web/v1/ml/anchors/${anchorId}`,
      headers: await this.getAuthorizedHeaders(true),
      data: anchor,
    });
  }

  async getAnomalyNotes(anomalyId: string): Promise<AnomalyNotePayload[]> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/ml/anomalies/${anomalyId}/notes`,
      headers: await this.getAuthorizedHeaders(),
    });

    return this.normalizeNotesPayload(response.data);
  }

  async createAnomalyNote(anomalyId: string, note: AnomalyNotePayload): Promise<void> {
    await this.axiosInstance({
      method: "POST",
      url: `web/v1/ml/anomalies/${anomalyId}/notes`,
      headers: await this.getAuthorizedHeaders(true),
      data: note,
    });
  }

  async updateAnomalyNote(anomalyId: string, noteId: string, payload: UpdateAnomalyNoteBackendPayload): Promise<void> {
    await this.axiosInstance({
      method: "PATCH",
      url: `web/v1/ml/anomalies/${anomalyId}/notes/${noteId}`,
      headers: await this.getAuthorizedHeaders(true),
      data: payload,
    });
  }

  async deleteAnomalyNote(anomalyId: string, noteId: string): Promise<void> {
    await this.axiosInstance({
      method: "DELETE",
      url: `web/v1/ml/anomalies/${anomalyId}/notes/${noteId}`,
      headers: await this.getAuthorizedHeaders(),
    });
  }

  async getAnchorNotes(anchorId: string): Promise<AnomalyNotePayload[]> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/ml/anchors/${anchorId}/notes`,
      headers: await this.getAuthorizedHeaders(),
    });

    return this.normalizeNotesPayload(response.data);
  }

  async createAnchorNote(anchorId: string, note: AnomalyNotePayload): Promise<void> {
    await this.axiosInstance({
      method: "POST",
      url: `web/v1/ml/anchors/${anchorId}/notes`,
      headers: await this.getAuthorizedHeaders(true),
      data: note,
    });
  }

  async updateAnchorNote(anchorId: string, noteId: string, payload: UpdateAnchorNoteBackendPayload): Promise<void> {
    await this.axiosInstance({
      method: "PATCH",
      url: `web/v1/ml/anchors/${anchorId}/notes/${noteId}`,
      headers: await this.getAuthorizedHeaders(true),
      data: payload,
    });
  }

  async deleteAnchorNote(anchorId: string, noteId: string): Promise<void> {
    await this.axiosInstance({
      method: "DELETE",
      url: `web/v1/ml/anchors/${anchorId}/notes/${noteId}`,
      headers: await this.getAuthorizedHeaders(),
    });
  }

  async deleteAnomaly(persistedId: string): Promise<void> {
    await this.axiosInstance({
      method: "DELETE",
      url: `web/v1/ml/anomalies/${persistedId}`,
      headers: await this.getAuthorizedHeaders(),
    });
  }

  async deleteAnchor(anchorId: string): Promise<void> {
    await this.axiosInstance({
      method: "DELETE",
      url: `web/v1/ml/anchors/${anchorId}`,
      headers: await this.getAuthorizedHeaders(),
    });
  }
}

const anomalyApiService = new AnomalyApiService();
export default anomalyApiService;
